import Link from 'next/link';

import { Box, Button, Heading, Paragraph, Popover } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../constants/ListingSummaryConstants';
import { getCreateListingRoute, getListingRoute } from '../../../constants/urls';

import type { ReactElement } from 'react';
import type { PartialMessageTextAndUrlDto } from '@zg-rentals/ts-rental-manager-api-client';

const getCTARoute = (
  listingAlias: string,
  status: string,
  canResumeModularListingCreation: boolean,
  hasListingBeenActivated: boolean,
) => {
  if (canResumeModularListingCreation) {
    return getCreateListingRoute({ listingAlias });
  }

  const useListingRoute = status !== LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE || hasListingBeenActivated;
  return useListingRoute ? getListingRoute({ listingAlias }) : getCreateListingRoute({ listingAlias });
};

type Props = {
  listingAlias: string;
  isActive: boolean;
  canDeactivate: boolean;
  canResumeModularListingCreation: boolean;
  status: string;
  lastActivatedMs: number | undefined;
  isUpdateActiveStatusAllowed: boolean;
  isStreetEasyRestricted?: boolean;
  partialMessages: Array<PartialMessageTextAndUrlDto>;
  isLoading: boolean;
  onDeactivateClick: () => void;
};

const CTA = ({
  listingAlias,
  isActive,
  canDeactivate,
  canResumeModularListingCreation,
  status,
  lastActivatedMs,
  isUpdateActiveStatusAllowed,
  isStreetEasyRestricted = false,
  partialMessages,
  isLoading,
  onDeactivateClick,
}: Props) => {
  const hasListingBeenActivated = Boolean(lastActivatedMs);
  const ctaPath = getCTARoute(listingAlias, status, canResumeModularListingCreation, hasListingBeenActivated);
  const ctaTitle = canResumeModularListingCreation ? 'Resume' : 'List your property';

  const isActiveAndUpdateNotAllowed = isActive && !isUpdateActiveStatusAllowed;

  const handleDeactivateClick = () => {
    if (isActiveAndUpdateNotAllowed) return;
    onDeactivateClick();
  };

  const renderDeactivateButton = () => (
    <Button
      tone={isUpdateActiveStatusAllowed ? 'brand' : 'neutral'}
      emphasis={isUpdateActiveStatusAllowed ? 'filled' : 'outlined'}
      disabled={!canDeactivate}
      fluid={true}
      onClick={handleDeactivateClick}
      data-testid="deactivate-button"
      loading={isLoading}
      css={{ marginLeft: '300' }}
    >
      Deactivate
    </Button>
  );

  const withUnableToDeactivatePopover = (component: ReactElement) => (
    <Popover
      trigger={component}
      tone="info"
      header={<Heading level={6}>Unable to deactivate</Heading>}
      body={
        <Paragraph>
          <PartialMessage partialMessages={partialMessages} />
        </Paragraph>
      }
    />
  );

  if (status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE) {
    if (isStreetEasyRestricted) {
      return null;
    }

    return (
      <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          tone="brand"
          emphasis="filled"
          asChild={!isLoading}
          data-testid={
            canResumeModularListingCreation ? 'connected-listing-resume-cta' : 'connect-listing-list-my-property-cta'
          }
          loading={isLoading}
        >
          {isLoading ? ctaTitle : <Link href={ctaPath}>{ctaTitle}</Link>}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
      aria-label="edit and deactivate"
    >
      <Button fluid={true} asChild={!isLoading} loading={isLoading}>
        {isLoading ? 'Edit' : <Link href={ctaPath}>Edit</Link>}
      </Button>
      {isActiveAndUpdateNotAllowed ? withUnableToDeactivatePopover(renderDeactivateButton()) : renderDeactivateButton()}
    </Box>
  );
};

export default CTA;
